// src/pages/TapisListPage/index.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import { getCompetitionById } from "../../services/competitionsServices";
import { getCompetitionDistributions } from "../../services/distributionsServices";
import { countDistributionCombats } from "../../services/combatsServices";

import Breadcrumb from "../../components/Breadcrumb";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import useScrollToTop from "../../hooks/useScrollToTop";

import {
  FaSyncAlt,
  FaCheckCircle,
  FaClock,
  FaSortNumericDown,
  FaSortNumericUp,
  FaFilter,
  FaSearch,
  FaArrowRight,
} from "react-icons/fa";

/* -----------------------------------------------------------
   Utilitaires
----------------------------------------------------------- */

const safeParse = (val) => {
  if (Array.isArray(val)) return val;
  try {
    const p = JSON.parse(val || "[]");
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};

// Nombre max de combats par formule (poules).
// (Pour les tableaux on retourne 0 → inconnu)
const getMaxCombats = (formule) => {
  switch (formule) {
    case "poule_2":
      return 2;
    case "poule_3":
      return 3;
    case "poule_4":
      return 6;
    case "poule_5":
      return 10;
    case "poule_6":
      return 12;
    default:
      return 0; // tableaux & autres
  }
};

const isBracketFormula = (formule = "") => /^tableau_/i.test(String(formule));

// Palette selon ratio de combats restants (par distribution poule)
const distBadgeClasses = (remaining, max) => {
  if (max <= 0)
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  const ratio = remaining / max; // 0..1
  if (remaining === 0) {
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  }
  if (ratio > 0.66) {
    return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-200";
  }
  if (ratio > 0.33) {
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200";
  }
  return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200";
};

// Classe de la colonne de gauche (numéro de tapis)
const matStripClasses = (isActive) =>
  isActive
    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200"
    : "bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300";

// Progress bar classes
const progressBarClasses = (percent) => {
  if (percent >= 100) return "bg-slate-400 dark:bg-slate-600";
  if (percent >= 66) return "bg-emerald-500";
  if (percent >= 33) return "bg-amber-500";
  return "bg-red-500";
};

/* -----------------------------------------------------------
   Composant principal
----------------------------------------------------------- */

const TapisListPage = () => {
  useScrollToTop();
  const { competition_id } = useParams();

  const [competition, setCompetition] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [combatsByDistribution, setCombatsByDistribution] = useState({});

  // UI state (filtre / tri / recherche)
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | done
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  const toggleSortDir = () => setSortDir((d) => (d === "asc" ? "desc" : "asc"));

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const distributionLocalNumberMap = useMemo(() => {
    const sorted = [...(distributions || [])].sort((a, b) => {
      const ai = Number(a.id);
      const bi = Number(b.id);

      if (!Number.isNaN(ai) && !Number.isNaN(bi)) return ai - bi;

      return String(a.id).localeCompare(String(b.id), "fr", {
        numeric: true,
        sensitivity: "base",
      });
    });

    const map = new Map();

    sorted.forEach((d, index) => {
      const key = Number(d.id) || d.id;
      map.set(key, index + 1);
    });

    return map;
  }, [distributions]);

  const getLocalDistNumber = useCallback(
    (distId) => {
      const key = Number(distId) || distId;
      return distributionLocalNumberMap.get(key) ?? "—";
    },
    [distributionLocalNumberMap],
  );

  // Fetch initial
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [c, dists] = await Promise.all([
          getCompetitionById(competition_id),
          getCompetitionDistributions(competition_id),
        ]);

        setCompetition(c || null);
        setDistributions(dists || []);
        await fetchCombatsCounts(dists || []);
      } catch (e) {
        toast.error("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [competition_id]);

  // Rafraîchir (manuel) — memoïsé pour le setInterval
  const refresh = useCallback(async () => {
    try {
      setRefreshing(true);
      const dists = await getCompetitionDistributions(competition_id);
      setDistributions(dists || []);
      await fetchCombatsCounts(dists || []);
    } catch {
      toast.error("Impossible de rafraîchir les données.");
    } finally {
      setRefreshing(false);
    }
  }, [competition_id]);

  // Auto-refresh toutes les 15s si activé
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      refresh();
    }, 15000);
    return () => clearInterval(id);
  }, [autoRefresh, refresh]);

  // Charger les compteurs de combats par distribution
  const fetchCombatsCounts = async (distributionsList) => {
    const counts = {};
    for (const dist of distributionsList) {
      try {
        const { count } = await countDistributionCombats(dist.id);
        counts[dist.id] = count;
      } catch {
        counts[dist.id] = 0;
      }
    }
    setCombatsByDistribution(counts);
  };

  // Regrouper par tapis + métriques agrégées
  const mats = useMemo(() => {
    // Regroupement par numero_tapis (on ignore les distributions terminées si is_completed === true)
    const groups = new Map();
    for (const dist of distributions) {
      if (dist.is_completed === true) continue; // adapte selon ton besoin
      const mat = Number(dist.numero_tapis);
      if (!groups.has(mat)) groups.set(mat, []);
      groups.get(mat).push(dist);
    }

    const items = [];
    for (const [matNumber, dists] of groups.entries()) {
      // Agréger combats
      let maxTotal = 0; // somme des max (poules)
      let doneTotal = 0; // combats réalisés (poules)
      let tableauDoneTotal = 0; // combats réalisés (tableaux uniquement)

      const enrichedDists = dists.map((dist) => {
        const done = combatsByDistribution[dist.id] ?? 0;
        const max = getMaxCombats(dist.formule_repartition);
        const remaining = Math.max(0, max - done);

        // Agrégation
        if (max > 0) {
          maxTotal += max;
          doneTotal += Math.min(done, max);
        } else {
          // Tableau → pas de max, on additionne à part
          tableauDoneTotal += done;
        }

        return {
          ...dist,
          _done: done,
          _max: max,
          _remaining: remaining,
          _isBracket: isBracketFormula(dist.formule_repartition),
          _competitorsCount: safeParse(dist.competiteurs).length,
        };
      });

      const remainingTotal = Math.max(0, maxTotal - doneTotal);
      // Si on a des poules → % basé sur poules ; sinon 0 par défaut
      const percent =
        maxTotal > 0 ? Math.round((doneTotal / maxTotal) * 100) : 0;

      // ⚠️ Statut Actif
      // - Actif si (poules avec combats restants) OU (au moins 1 combat effectué dans un tableau)
      const isActive =
        (maxTotal > 0 && remainingTotal > 0) || tableauDoneTotal > 0;

      items.push({
        matNumber,
        dists: enrichedDists,
        maxTotal,
        doneTotal,
        remainingTotal,
        percent,
        isActive,
        tableauDoneTotal,
      });
    }

    // Filtre recherche par n° de tapis
    let filtered = items.filter((m) => {
      if (!query.trim()) return true;
      return String(m.matNumber).includes(query.trim());
    });

    // Filtre statut
    filtered = filtered.filter((m) => {
      if (statusFilter === "active") return m.isActive;
      if (statusFilter === "done") return !m.isActive;
      return true;
    });

    // Tri par numéro
    filtered.sort((a, b) =>
      sortDir === "asc" ? a.matNumber - b.matNumber : b.matNumber - a.matNumber,
    );

    return filtered;
  }, [distributions, combatsByDistribution, query, statusFilter, sortDir]);

  if (loading || !competition) {
    return (
      <div className="flex flex-col w-full mx-auto p-2 mt-[130px] dark:text-white">
        <ScrollIndicator />
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-64 bg-gray-200 dark:bg-slate-800 rounded" />
            <div className="h-10 w-full bg-gray-200 dark:bg-slate-800 rounded" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="h-48 bg-gray-200 dark:bg-slate-800 rounded-xl" />
              <div className="h-48 bg-gray-200 dark:bg-slate-800 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* -----------------------------------------------------------
     Rendu
  ----------------------------------------------------------- */

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] dark:text-white">
      <ScrollIndicator />

      <div className="text-center space-y-2 mb-6">
        <h2 className="text-sm uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Organisateur
        </h2>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          RÉPARTITION DES DISTRIBUTIONS PAR TAPIS
        </h1>
      </div>

      <div className="mb-4 max-w-6xl mx-auto w-full">
        <Breadcrumb competitionName={competition.nom_competition} />
      </div>

      {/* Toolbar */}
      <div className="max-w-6xl mx-auto w-full mb-6">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between p-3 rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white dark:bg-slate-900 shadow">
          <div className="flex items-center gap-2">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                placeholder="Rechercher un n° de tapis…"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-2 text-sm px-2 py-1 rounded-lg border border-gray-300 dark:border-slate-600">
                <FaFilter />
                Statut :
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-transparent outline-none"
                >
                  <option value="all">Tous</option>
                  <option value="active">Actifs</option>
                  <option value="done">Terminés</option>
                </select>
              </span>

              <button
                onClick={toggleSortDir}
                className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800"
                title="Trier par n° de tapis"
              >
                {sortDir === "asc" ? (
                  <FaSortNumericDown />
                ) : (
                  <FaSortNumericUp />
                )}
                Tri n°
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
              title="Rafraîchir"
            >
              <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
              Rafraîchir
            </button>
            <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh 15s
            </label>
          </div>
        </div>

        {/* Légende */}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
          <span className="inline-flex items-center gap-2">
            <span className="h-3 w-5 rounded bg-emerald-200 border border-emerald-300/60 dark:bg-emerald-900/30 dark:border-emerald-800" />
            Tapis actif (combats restants ou tableaux en cours)
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-3 w-5 rounded bg-gray-200 border border-gray-300/70 dark:bg-slate-800 dark:border-slate-700" />
            Tapis inactif (distributions terminées ou non commencées)
          </span>
        </div>
      </div>

      {/* Grille des tapis */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-6xl mx-auto mb-10">
        {mats.length > 0 ? (
          mats.map((mat) => {
            const stripCls = matStripClasses(mat.isActive);
            const progressPercent = Math.min(100, Math.max(0, mat.percent));
            const hasPouleProgress = mat.maxTotal > 0;

            return (
              <Link
                key={mat.matNumber}
                to={`/competition/${competition_id}/tapis/${mat.matNumber}`}
                className="block rounded-2xl overflow-hidden border border-gray-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-900 shadow-sm hover:shadow-xl transition"
              >
                <div className="flex h-full">
                  {/* Bande gauche : n° tapis + statut */}
                  <div
                    className={`w-[110px] ${stripCls} flex flex-col items-center justify-center p-4`}
                  >
                    <p className="text-xs opacity-75">Tapis</p>
                    <p className="text-6xl font-extrabold leading-none">
                      {mat.matNumber}
                    </p>
                    <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold">
                      {mat.isActive ? (
                        <>
                          <FaClock /> Actif
                        </>
                      ) : (
                        <>
                          <FaCheckCircle /> Terminé
                        </>
                      )}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="flex-1 p-4 flex flex-col gap-3">
                    {/* Header droits : stats + CTA */}
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        {hasPouleProgress ? (
                          <>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                              Combats restants (agrégés)
                            </div>
                            <div className="text-xl font-bold">
                              {mat.remainingTotal}{" "}
                              <span className="text-sm font-normal text-gray-500">
                                / {mat.maxTotal}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                              Combats effectués (tableaux)
                            </div>
                            <div className="text-xl font-bold">
                              {mat.tableauDoneTotal}
                            </div>
                          </>
                        )}
                      </div>
                      <div className="shrink-0">
                        <span className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-blue-600 text-white">
                          Voir détails <FaArrowRight />
                        </span>
                      </div>
                    </div>

                    {/* Progress bar (affichée uniquement si poules présentes) */}
                    {hasPouleProgress && (
                      <div className="w-full">
                        <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-slate-800 overflow-hidden">
                          <div
                            className={`h-2 ${progressBarClasses(
                              progressPercent,
                            )}`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Progression : {progressPercent}%
                        </div>
                      </div>
                    )}

                    {/* Liste des distributions du tapis */}
                    <div className="mt-1">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Distributions :
                      </p>
                      <ul className="text-sm text-gray-800 dark:text-gray-200 space-y-2">
                        {mat.dists.length > 0 ? (
                          mat.dists.map((dist) => {
                            const remaining = dist._remaining;
                            const max = dist._max;
                            const badgeCls = dist._isBracket
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200"
                              : distBadgeClasses(remaining, max);

                            const typeLabel = dist.formule_repartition
                              ?.replace("tableau_base_", "Tableau ")
                              ?.replace("poule_", "Poule ");

                            return (
                              <li
                                key={dist.id}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-lg p-3 bg-gray-50/60 dark:bg-slate-800/60"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold truncate">
                                      <span
                                        className="text-gray-400 mr-2"
                                        title={`ID technique : ${dist.id}`}
                                      >
                                        #{getLocalDistNumber(dist.id)}
                                      </span>
                                      {dist.genre} • {dist.categorie_age} •{" "}
                                      {dist.categorie_poids}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-300">
                                      {typeLabel} {" · "}{" "}
                                      {dist._competitorsCount} compétiteur(s)
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2 shrink-0">
                                    {/* Badge combats : 
                                        - Poule → done/max
                                        - Tableau → juste done (ex: "5 combats")
                                    */}
                                    <span
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${badgeCls}`}
                                    >
                                      {dist._isBracket ? (
                                        <>
                                          {dist._done} combat
                                          {dist._done > 1 ? "s" : ""}
                                        </>
                                      ) : (
                                        <>
                                          {dist._done}/{max}
                                        </>
                                      )}
                                    </span>
                                  </div>
                                </div>

                                {/* Alerte “derniers combats” uniquement pour poules */}
                                {!dist._isBracket &&
                                  max > 0 &&
                                  max - dist._done <= 2 &&
                                  dist._done !== max && (
                                    <div className="mt-2 text-xs text-amber-800 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-1 rounded text-center">
                                      ⚠️ Derniers combats à effectuer
                                    </div>
                                  )}
                              </li>
                            );
                          })
                        ) : (
                          <li className="italic text-gray-400">
                            Aucune répartition
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
          <div className="col-span-1 sm:col-span-2 text-center text-gray-500 dark:text-gray-400">
            Aucun tapis trouvé.
          </div>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default TapisListPage;
